// useTimerEntrySaved(callback)
//
// Fires `callback` whenever the user saves (or updates) a time entry — from
// any window in the Electron session, in any browser tab.  Wraps the two
// signal mechanisms:
//
//   1. `window.dispatchEvent(new Event('timerEntrySaved'))` — same-window
//      signal fired by TimerWidget after a successful save.  Legacy in-window
//      popup timer + the standalone pop-out both fire this within their own
//      window.
//   2. `localStorage.setItem('lextrack_timer_broadcast', ...)` — cross-window
//      signal.  A `storage` event fires in every OTHER same-origin renderer,
//      so the main window sees the update when a pop-out timer saves.
//
// Any page that shows time-related data (Dashboard summary tiles, Dailies,
// Billing, Matter Detail, Time Entries list) should call this hook with its
// own refresh function.  Without it, saving a time entry in a pop-out window
// leaves the parent view showing stale data until the user manually reloads.
//
// The hook uses a ref so `callback` doesn't need a stable identity — pass an
// inline arrow function without worrying about re-registering listeners on
// every render.

import { useEffect, useRef } from 'react'

export default function useTimerEntrySaved (callback) {
  const cbRef = useRef(callback)
  useEffect(() => { cbRef.current = callback }, [callback])

  useEffect(() => {
    const same = () => { try { cbRef.current?.() } catch {} }
    const cross = (e) => {
      if (e.key !== 'lextrack_timer_broadcast' || !e.newValue) return
      try {
        const data = JSON.parse(e.newValue)
        if (data?.type === 'saved') cbRef.current?.()
      } catch {}
    }
    window.addEventListener('timerEntrySaved', same)
    window.addEventListener('storage', cross)
    return () => {
      window.removeEventListener('timerEntrySaved', same)
      window.removeEventListener('storage', cross)
    }
  }, [])
}
